import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, QueryList, ViewChildren } from '@angular/core';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ImageModule } from '@df/module-image/image.module';
import { NgInject } from '@df/ng/ng-inject';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { defaultImage } from 'projects/app-default/src/app/icms.config';
import { ScrollNavTargetDirective } from 'projects/app-default/src/app/ui/scroll-nav/scroll-nav-target.directive';
import { HotSpotZoomService } from '../../../hot-spot/hot-spot-image-zoom-service/hot-spot-image-zoom.service';
import { HotSpotComponent } from '../../../hot-spot/hot-spot.component';

export enum EDescriptionType {
    List = 'list',
    Text = 'text'
}

interface IDescriptionList {
    text: string;
}

export interface IHotSpot {
    title: string;
    zoomedImage?: string;
    descriptionType: EDescriptionType;
    descriptionText?: string;
    descriptionList?: IDescriptionList[];
    ctaCopy?: string;
    ctaLink?: string;
    showIcon?: boolean;
    icon?: string;
    verticalPosition: number;
    horizontalPosition: number;
}

export interface ITabs {
    tabTitle: string;
    title: string;
    showIcon?: boolean;
    icon?: string;
    description?: string;
    ctaCopy?: string;
    ctaLink?: string;
    image: string;
    hotSpots: IHotSpot[];
}

export interface IProductFeaturesContent {
    title?: string;
    productFeaturesTabs: ITabs[];
}

let uid = 0;

@ICmsComponent<ProductFeaturesComponent>({
    id: 'page-component-product-features',
    name: 'Product features',
    description: 'Product features layout',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'What Makes Our Saddle Pads Great'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'productFeaturesTabs',
            title: 'Product features tabs',
            itemName: 'Tab',
            itemNameField: 'tabTitle',
            defaultSize: 2,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'tabTitle',
                    title: 'Tab title',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',

                    required: true
                },
                {
                    key: 'showIcon',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Show icon'
                },
                {
                    key: 'icon',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Icon',
                    canShow: (data: ITabs) => data?.showIcon
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'description',
                    title: 'Description'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'ctaCopy',
                    title: 'Cta copy'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'ctaLink',
                    title: 'Cta link'
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'hotSpots',
                    title: 'Hotspots',
                    itemName: 'Hotspot',
                    itemNameField: 'title',
                    fields: [
                        {
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Title'
                        },
                        {
                            key: 'zoomedImage',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Zoomed image',
                            description: 'Image that will be shown when the user clicks the hotspot'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'descriptionType',
                            type: NICmsContentField.EFieldType.choice,
                            options: [
                                { key: EDescriptionType.Text, title: 'Description: Text' },
                                { key: EDescriptionType.List, title: 'Description: List' }
                            ],
                            default: EDescriptionType.Text
                        },
                        {
                            key: 'descriptionText',
                            type: NICmsContentField.EFieldType.textarea,
                            title: 'Description Text',
                            canShow: (data: IHotSpot) => data.descriptionType === EDescriptionType.Text
                        },
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'descriptionList',
                            title: 'Description List',
                            itemName: 'text',
                            fields: [
                                {
                                    key: 'text',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Text',
                                    required: true
                                }
                            ],
                            canShow: (data: IHotSpot) => data.descriptionType === EDescriptionType.List
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'ctaCopy',
                            title: 'Cta copy'
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'ctaLink',
                            title: 'Cta link'
                        },
                        {
                            key: 'showIcon',
                            type: NICmsContentField.EFieldType.toggle,
                            title: 'Show icon'
                        },
                        {
                            key: 'icon',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Icon',
                            canShow: (data: IHotSpot) => data.showIcon
                        },
                        {
                            key: 'verticalPosition',
                            type: NICmsContentField.EFieldType.number,
                            title: 'Button Vertical Position (%)',
                            default: 0
                        },
                        {
                            key: 'horizontalPosition',
                            type: NICmsContentField.EFieldType.number,
                            title: 'Button Horizontal Position (%)',
                            default: 0
                        }
                    ]
                }
            ],
            default: [
                {
                    hotSpots: [
                        {
                            descriptionType: 'text',
                            descriptionList: [],
                            verticalPosition: 50,
                            horizontalPosition: 10,
                            title: 'Bamboo Lining',
                            descriptionText:
                                'The 100% natural Bamboo material provides sweat wicking properties and works to regulate heat and friction.'
                        },
                        {
                            descriptionType: 'text',
                            descriptionList: [],
                            verticalPosition: 76,
                            horizontalPosition: 42,
                            title: 'Girth Protection',
                            descriptionText:
                                'The textured PU leather girth protection area creates a base for the girth straps to sit securely.'
                        },
                        {
                            descriptionType: 'text',
                            descriptionList: [],
                            verticalPosition: 82,
                            horizontalPosition: 57,
                            title: 'Locking Girth Straps',
                            descriptionText: 'An innovative design created to offer more girthing options and increased stability. '
                        }
                    ],
                    tabTitle: 'Loire',
                    title: 'Satin Loire',
                    description:
                        'Woven Satin fabric gives a beautiful sheen to these pads whilst still benefitting from the everyday use practical features. ',
                    image: defaultImage,
                    ctaCopy: 'Shop Now',
                    ctaLink: '/'
                },
                {
                    hotSpots: [
                        {
                            descriptionType: 'text',
                            descriptionList: [],
                            verticalPosition: 37,
                            horizontalPosition: 4,
                            title: 'Bamboo Lining',
                            descriptionText:
                                'The 100% natural Bamboo material provides sweat wicking properties and works to regulate heat and friction.'
                        },
                        {
                            descriptionType: 'text',
                            descriptionList: [],
                            verticalPosition: 64,
                            horizontalPosition: 46,
                            title: 'Girth Protection',
                            descriptionText:
                                'The textured PU leather girth protection area creates a base for the girth straps to sit securely.'
                        }
                    ],
                    tabTitle: 'Suede',
                    image: defaultImage,
                    description:
                        "These saddle pads offer a sleek and professional look to anyone's saddle, whilst working as a durable, everyday saddle pad to use for every occasion. ",
                    title: 'Suede',
                    ctaCopy: 'Shop Now',
                    ctaLink: '/'
                }
            ]
        }
    ]
})
@Component({
    selector: 'product-features',
    templateUrl: './product-features.html',
    standalone: true,
    imports: [
        DragScrollDirective,
        HotSpotComponent,
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        NgTemplateOutlet,
        ScrollNavTargetDirective,
        ToggleModule,
        TranslationModule,
        ZippyModule
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./product-features.scss']
})
export class ProductFeaturesComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IProductFeaturesContent;

    private hotSpotZoomService = inject(HotSpotZoomService);

    @ViewChildren('mainImage', { read: ElementRef })
    _mainImages!: QueryList<ElementRef>;

    mainImagesArray?: ElementRef[];
    protected uid = uid++;

    setMainImagesArray(): void {
        this.mainImagesArray = this._mainImages.toArray();
    }

    protected hotSpotClickHandler(tabIndex: number, x: number, y: number): void {
        this.setMainImagesArray();
        if (this.mainImagesArray?.length) {
            this.hotSpotZoomService.zoomToPoint(this.mainImagesArray[tabIndex]?.nativeElement, x, y, 1.5);
        }
    }

    protected unzoom(tabIndex: number) {
        this.setMainImagesArray();
        if (this.mainImagesArray?.length) {
            this.hotSpotZoomService.unzoom(this.mainImagesArray[tabIndex]?.nativeElement);
        }
    }

    get productFeatures(): ITabs[] {
        return this.component.productFeaturesTabs || [];
    }
}
